import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  input,
  output,
  viewChild,
  ElementRef,
  computed,
  effect,
  untracked,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import {BoosterStateService} from '@services/card-game/booster-state.service';
import {CARDS_TRIGGER, VIDEO_VOLUME} from '@/app/core/models/collection/booster.constants';
import {CardsLayerComponent} from '@/app/pages/tcg/cards-layer/cards-layer';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {CARD_H} from '@/app/core/models/collection/booster.constants';
import {CardService} from '@services/card-game/card.service';
import {AuthService} from '@services/auth.service';
import {ToastService} from '@services/toast.service';
import {HttpErrorResponse} from '@angular/common/http';

@Component({
	selector: 'peps-booster-opening',
	standalone: true,
	imports: [CardsLayerComponent, TranslatePipe],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: 'booster-opening.html',
})
export class BoosterOpeningComponent implements OnInit, OnDestroy {
	private readonly videoRef = viewChild.required<ElementRef<HTMLVideoElement>>('animVideo');
	private readonly cardsLayer = viewChild.required<CardsLayerComponent>('cardsLayer');
	private readonly stateService = inject(BoosterStateService);
	private readonly cardService = inject(CardService);
	private readonly authService = inject(AuthService);
	private readonly toastService = inject(ToastService);
	private readonly translate = inject(TranslateService);

	readonly cardHeight = CARD_H;
	readonly slots = computed(() => this.cardsLayer().slots());
	/** Cartes tirées, avec leur emplacement : seules celles déjà possédées reçoivent badge + décraft. */
	readonly ownedCards = computed(() => {
		const slots = this.slots();
		const revealed = this.cardsLayer().revealed();
		return this.stateService.cards()
			.map((drawn, index) => ({drawn, index, slot: slots[index]}))
			.filter(e => e.slot && revealed.has(e.index) && e.drawn.bestOwnedScore !== null);
	});
	/** PepsCoins rendus par rareté — absent pour une rareté non décraftable (ex. COLLECTOR). */
	readonly destroyRewards = signal<Partial<Record<string, number>>>({});
	/** Indices (dans le tirage) des cartes déjà décraftées, ou en cours de décraft. */
	readonly destroyedIndexes = signal<ReadonlySet<number>>(new Set());
	private readonly destroyingIndexes = new Set<number>();

	readonly isOpen = computed(() => this.stateService.state() !== 'idle');
	readonly isDone = computed(() => this.stateService.state() === 'done');

	/** true si l'utilisateur a encore un booster à ouvrir → affiche "ouvrir le suivant" à côté de "fermer". */
	readonly hasNextBooster = input(false);
	/** Demande d'ouverture du booster suivant — le parent enchaîne (setCards + open). */
	readonly openNext = output<void>();

	private cardsTriggered = false;

	constructor() {
		effect(() => {
			const s = this.stateService.state();
			if (s !== 'video') return;
			untracked(() => {
				this.cardsTriggered = false;
				this.cardsLayer().build();
				const video = this.videoRef().nativeElement;
				const src = this.getVideoSrc();
				if (!video.src.endsWith(src)) video.src = src;
				video.currentTime = 0;
				video.play();
			});
		});
	}

	ngOnInit(): void {
		this.cardService.getDestroyRewards().subscribe({
			next: (rewards) => this.destroyRewards.set(rewards),
			error: () => {},
		});
		const video = this.videoRef().nativeElement;
		video.src = this.getVideoSrc();
		video.loop = false;
		video.muted = false;
		video.volume = VIDEO_VOLUME;
		video.addEventListener('timeupdate', this.onTimeUpdate);
		video.addEventListener('ended', this.onVideoEnded);
	}

	private getVideoSrc(): string {
		return window.matchMedia('(min-width: 640px)').matches
			? 'assets/video/booster_opening.webm'
			: 'assets/video/booster_opening_mobile.webm';
	}

	/** Décraft du doublon tiré : détruit l'exemplaire créé/incrémenté par ce tirage contre des PepsCoins. */
	destroyDuplicate(index: number): void {
		const drawn = this.stateService.cards()[index];
		if (!drawn || this.destroyedIndexes().has(index) || this.destroyingIndexes.has(index)) return;
		this.destroyingIndexes.add(index);
		this.cardService.destroyCard(drawn.userCardId).subscribe({
			next: (result) => {
				this.destroyingIndexes.delete(index);
				this.destroyedIndexes.update(set => new Set(set).add(index));
				this.toastService.show(
					this.translate.instant('page.booster-opening.destroy-success', {coins: result.coinsEarned}),
					'success', 5000,
				);
				this.authService.loadUser().subscribe({error: () => {}});
			},
			error: (err: HttpErrorResponse) => {
				this.destroyingIndexes.delete(index);
				this.toastService.show(err.error?.message || this.translate.instant('page.booster-opening.destroy-error'), 'error', 5000);
			},
		});
	}

	close(): void {
		this.destroyedIndexes.set(new Set());
		this.destroyingIndexes.clear();
		this.cardsLayer().reset();
		const video = this.videoRef().nativeElement;
		video.pause();
		this.cardsTriggered = false;
		this.stateService.close();
	}

	onBackdropClick(e: MouseEvent): void {
		if (e.target === e.currentTarget) this.close();
	}

	/** Enchaîne sur le booster suivant : on nettoie la couche cartes, le parent relance l'ouverture. */
	next(): void {
		this.destroyedIndexes.set(new Set());
		this.destroyingIndexes.clear();
		this.cardsLayer().reset();
		this.cardsTriggered = false;
		this.openNext.emit();
	}

	private readonly onTimeUpdate = (): void => {
		if (this.stateService.state() === 'idle') return;
		const video = this.videoRef().nativeElement;
		if (!this.cardsTriggered && video.duration && video.currentTime >= video.duration - CARDS_TRIGGER) {
			this.cardsTriggered = true;
			this.cardsLayer().launchCascade();
		}
	};

	private readonly onVideoEnded = (): void => {
		if (this.stateService.state() === 'idle') return;
		this.cardsLayer().launchCascade();
	};

	ngOnDestroy(): void {
		const video = this.videoRef().nativeElement;
		video.removeEventListener('timeupdate', this.onTimeUpdate);
		video.removeEventListener('ended', this.onVideoEnded);
	}
}
